import { z } from 'zod';
import * as t from 'io-ts';
import { Logger } from '../../logger';
import { Cache } from '../cache/types';

export interface IMetroClient {
  getAvailableAppointmentSlots: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<AvailableTimeSlotsResponse | StatusMessageResponse>;
  scheduleAppointment: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    date: Date,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<StatusMessageResponse>;
  rescheduleAppointment: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    date: Date,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<StatusMessageResponse>;
  updateContactInfo: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    postData: IUpdateContactInfoPostData,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<StatusMessageResponse>;
  getScheduledAppointment: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<GetScheduledAppointmentResponse | StatusMessageResponse>;
}

interface IWeekofMonth {
  weekNo: number;
  weekStartDate: string;
}

export interface IAvailableTimeSlotsPostData {
  trackingNo: string;
  zipCode?: string;
  scheduleType: ScheduleType;
}

export interface IScheduleDeliveryPostData {
  trackingNo: string;
  date: string;
  weekofMonth: IWeekofMonth | null;
  scheduleType: ScheduleType;
  webUserID: string;
}

export interface IGetScheduledAppointmentPostData {
  scheduleType: ScheduleType;
  trackingNo: string;
}

export interface IUpdateContactInfoPostData {
  trackingNo: string;
  email: string;
  phone: string;
  extension: string;
  mobile: string;
  updateFor: ScheduleType;
}

const appointmentStatusSchema = z.union([
  z.literal('Success'),
  z.literal('Failed'),
  z.literal('ALM'),
  z.literal('ANP'),
  z.literal('ANA'),
  z.literal('CAN'),
  z.literal('CAP'),
  z.literal('EM'),
  z.literal('DAS'),
  z.literal('DCO'),
  z.literal('DLC'),
  z.literal('DH'),
  z.literal('PAS'),
  z.literal('PCO'),
  z.literal('PH'),
  z.literal('PLC'),
  z.literal('PNC'),
  z.literal('POH'),
  z.literal('RNA'),
  z.literal('RQD'),
  z.literal('SWXD'),
  z.literal('TNW'),
  z.literal('TR'),
]);

// 1 - Pickup, 2 - Delivery
const scheduleTypeSchema = z.union([z.literal('1'), z.literal('2')]);

type ScheduleType = z.infer<typeof scheduleTypeSchema>;

export const authorizationTokenResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.literal('bearer'),
  expires_in: z.number(),
  'as:client_id': z.string(),
  userName: z.string(),
  id: z.string(),
  refresh_token: z.string(),
  '.issued': z.string(),
  '.expires': z.string(),
});

export type AuthorizationTokenResponse = z.infer<
  typeof authorizationTokenResponseSchema
>;

const weekOfMonthSchema = z.object({
  weekNo: z.number(),
  weekStartDate: z.string(),
});

const availableWeekOfMonthSchema = z.object({
  weekNumber: z.string(),
  daysofMonth: z.string(),
});

export const statusMessageResponseSchema = z.object({
  status: appointmentStatusSchema,
  message: z.string(),
});

export type StatusMessageResponse = z.infer<typeof statusMessageResponseSchema>;

export const availableTimeSlotsResponseSchema = z.object({
  appointmentAvailable: z.union([z.literal(0), z.literal(1)]), // 0 - No, 1 - Yes
  exceptionDates: z.string().nullable(),
  datesofMonth: z.string(),
  weeksofMonth: z.array(availableWeekOfMonthSchema),
  specificDay: z.string().nullable(),
  scheduleType: scheduleTypeSchema,
});

export type AvailableTimeSlotsResponse = z.infer<
  typeof availableTimeSlotsResponseSchema
>;

export const getScheduledAppointmentResponseSchema = z.object({
  scheduledDate: z.string().nullable(),
  scheduledWeek: weekOfMonthSchema.nullable(),
});

export type GetScheduledAppointmentResponse = z.infer<
  typeof getScheduledAppointmentResponseSchema
>;

// io-ts validators for backward compatibility with UI code
const AppointmentStatusV = t.union([
  t.literal('Success'),
  t.literal('Failed'),
  t.literal('ALM'),
  t.literal('ANP'),
  t.literal('ANA'),
  t.literal('CAN'),
  t.literal('CAP'),
  t.literal('EM'),
  t.literal('DAS'),
  t.literal('DCO'),
  t.literal('DLC'),
  t.literal('DH'),
  t.literal('PAS'),
  t.literal('PCO'),
  t.literal('PH'),
  t.literal('PLC'),
  t.literal('PNC'),
  t.literal('POH'),
  t.literal('RNA'),
  t.literal('RQD'),
  t.literal('SWXD'),
  t.literal('TNW'),
  t.literal('TR'),
]);

export const StatusMessageResponseV = t.strict({
  status: AppointmentStatusV,
  message: t.string,
});
